// ============================================================================
// TAX REGISTRATIONS (Setup > Tax & Billing > Tax Registrations)
// ============================================================================
// Settings cleanup C4 (crs-doc/CRS-Setup-Navigation-and-Help-Design.md section 4): the tax
// jurisdiction of each legal entity, kept in CRS's own tax profile table (the org tables are the
// Tenant Hub projection and hold nothing application-specific). Properties and outlets are taxed
// under their entity's jurisdiction. Each change is audited on the server.
//
// Registration (setup design 4.5, 2026-10-28) decides whether tax is calculated for the entity's
// properties and outlets: Regular yes; Composition (India GST) or Not registered no. Not set: the
// Tax Calculation switch in Settings › Modules still decides.

import React, { useCallback, useEffect, useState } from 'react';
import notificationService from 'services/notificationService';
import { taxService } from 'api/services/taxService';
import type { EntityTaxJurisdiction, TaxJurisdictionOption, TaxRegistrationType } from 'api/types/tax.types';

const REGISTRATION_LABEL: Record<TaxRegistrationType, string> = {
  REGULAR: 'Regular — tax is calculated',
  COMPOSITION: 'Composition — no tax charged',
  NOT_REGISTERED: 'Not registered — no tax',
};

interface TaxRegistrationsScreenProps {
  tabId?: string;
  moduleName?: string;
  allowedActions?: string[];
}

const TaxRegistrationsScreen: React.FC<TaxRegistrationsScreenProps> = ({ allowedActions = [] }) => {
  const canEdit = allowedActions.includes('edit');

  const [entities, setEntities] = useState<EntityTaxJurisdiction[]>([]);
  const [jurisdictions, setJurisdictions] = useState<TaxJurisdictionOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [ents, opts] = await Promise.all([taxService.getEntityJurisdictions(), taxService.getJurisdictionOptions()]);
      setEntities(ents);
      setJurisdictions(opts);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || err?.message || 'The tax registrations could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  // Both fields are always sent: the server saves the pair.
  const save = useCallback(async (entity: EntityTaxJurisdiction, change: { code?: string; registration?: string }) => {
    setSavingId(entity.entityId);
    try {
      await taxService.setEntityJurisdiction(
        entity.entityId,
        (change.code ?? entity.taxJurisdictionCode ?? '') || null,
        (change.registration ?? entity.registrationType ?? '') || null,
      );
      notificationService.toast.success(`Tax registration of ${entity.entityName} saved.`);
      await load();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || err?.message || 'The tax registration could not be saved.');
    } finally {
      setSavingId(null);
    }
  }, [load]);

  return (
    <div className="module-master-container">
      <div className="setup-sheet">
        <div className="config-panel-header">
          <h2>Tax Registrations</h2>
          <p>
            Each property and outlet is taxed under the jurisdiction of its legal entity. The entity&apos;s registration
            decides whether tax is calculated: Regular yes; Composition or Not registered no. Where it is not set, the Tax
            Calculation switch in Settings › Modules decides.
          </p>
        </div>

        <section className="setup-sheet-section">
          {loading ? (
            <div className="config-loading">Loading...</div>
          ) : entities.length === 0 ? (
            <div className="config-empty">No legal entities yet. Add them under Setup → Organisation → Entities.</div>
          ) : (
            <table className="setup-sheet-table">
              <thead>
                <tr><th>Entity</th><th>Properties</th><th>Tax jurisdiction</th><th>Registration</th></tr>
              </thead>
              <tbody>
                {entities.map((e) => (
                  <tr key={e.entityId}>
                    <td>{e.entityName}<div className="setup-sheet-note">{e.entityCode}</div></td>
                    <td>{e.propertyCount}</td>
                    <td>
                      <select
                        className="setup-sheet-select"
                        value={e.taxJurisdictionCode ?? ''}
                        disabled={!canEdit || savingId === e.entityId}
                        onChange={(ev) => void save(e, { code: ev.target.value })}
                      >
                        <option value="">Not set</option>
                        {jurisdictions.map((j) => (
                          <option key={j.jurisdictionCode} value={j.jurisdictionCode}>
                            {j.jurisdictionName} ({j.jurisdictionCode})
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <select
                        className="setup-sheet-select"
                        data-registration
                        value={e.registrationType ?? ''}
                        disabled={!canEdit || savingId === e.entityId}
                        onChange={(ev) => void save(e, { registration: ev.target.value })}
                      >
                        <option value="">Not set — the Tax Calculation switch decides</option>
                        {(e.registrationTypes ?? []).map((r) => (
                          <option key={r} value={r}>{REGISTRATION_LABEL[r] ?? r}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>
    </div>
  );
};

export default TaxRegistrationsScreen;
